// @refresh reset
/**
 * EventPady+ Authentication Context
 *
 * State held in memory only — never in localStorage.
 * Refresh token stored in memory tokenStore (cleared on page reload).
 *
 * Session restoration on app load:
 *   - Requires a refresh token in memory (only available within the same
 *     browser session, not after a hard reload). Users log in on fresh loads.
 *
 * Role hierarchy is encoded here so hasPermission() can be used anywhere.
 */
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  type ReactNode,
} from 'react'
import { apiClient, tokenStore } from '@/services/api/client'
import { API_ENDPOINTS } from '@/services/api/endpoints'
import type { User, Organization, UserRole } from '@/types'

// ---------------------------------------------------------------------------
// Role hierarchy
// ---------------------------------------------------------------------------
const ROLE_HIERARCHY: Record<UserRole, number> = {
  CAPTURE_AGENT: -1,  // capture-only; below READ_ONLY in hierarchy
  READ_ONLY: 0,
  STAFF: 1,
  ORG_ADMIN: 2,
  ORG_OWNER: 3,
  SUPER_ADMIN: 4,
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
interface AuthState {
  user: User | null
  organization: Organization | null
  role: UserRole | null
  isAuthenticated: boolean
  isLoading: boolean
}

export interface SessionPayload {
  access: string
  user: User
  organization: Organization
  role: UserRole
}

export interface AuthContextValue extends AuthState {
  login: (email: string, password: string) => Promise<void>
  logout: () => Promise<void>
  setSession: (payload: SessionPayload) => void
  hasPermission: (requiredRole: UserRole) => boolean
  refreshOrg: () => Promise<void>
}

// ---------------------------------------------------------------------------
// API response shapes
// ---------------------------------------------------------------------------
interface RefreshResponse {
  access_token?: string
  refresh_token?: string
}

interface MeResponse {
  user: User
  organization: Organization
  role: UserRole
}

interface LoginResponse {
  access_token?: string
  refresh_token?: string
}

function unwrapResponse<T>(payload: T | { data: T } | undefined): T | null {
  if (!payload) return null
  if (typeof payload === 'object' && payload !== null && 'data' in payload) {
    return (payload as { data: T }).data
  }
  return payload as T
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------
const AuthContext = createContext<AuthContextValue | null>(null)

export function useAuthContext(): AuthContextValue {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuthContext must be used within <AuthProvider>')
  }
  return context
}

// ---------------------------------------------------------------------------
// Shared state objects
// ---------------------------------------------------------------------------
const INITIAL_STATE: AuthState = {
  user: null,
  organization: null,
  role: null,
  isAuthenticated: false,
  isLoading: true,
}

const UNAUTHENTICATED: AuthState = {
  user: null,
  organization: null,
  role: null,
  isAuthenticated: false,
  isLoading: false,
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------
export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>(INITIAL_STATE)

  // Guards against React StrictMode double-invocation of the session-restore
  // effect. Both runs read the same refresh token before either has called the
  // backend; the first call rotates the token and the second would get 401.
  // Setting this synchronously (before any `await`) ensures the second
  // invocation bails out before making a network request.
  const lastAttemptedRefresh = useRef<string | null>(null)

  // ── Restore session on mount ────────────────────────────────────────────
  // Tries to restore using the in-memory refresh token (available within
  // the same browser tab session). On fresh page loads there is no token,
  // so we fall through to UNAUTHENTICATED immediately.
  useEffect(() => {
    let cancelled = false

    // Safety net: if restoreSession() hasn't resolved within 12 s (e.g. the
    // backend is unreachable and Axios timeout somehow didn't fire), force the
    // UNAUTHENTICATED state so the user sees the login page instead of an
    // infinite white-screen spinner.
    const safetyTimer = setTimeout(() => {
      if (!cancelled) {
        tokenStore.set(null)
        tokenStore.setRefresh(null)
        setState(UNAUTHENTICATED)
      }
    }, 12_000)

    async function restoreSession(): Promise<void> {
      const storedRefreshToken = tokenStore.getRefresh()

      if (!storedRefreshToken) {
        // No refresh token in memory — require fresh login
        if (!cancelled) setState(UNAUTHENTICATED)
        return
      }

      // Prevent StrictMode from consuming a rotated token twice
      if (lastAttemptedRefresh.current === storedRefreshToken) {
        return
      }
      lastAttemptedRefresh.current = storedRefreshToken

      try {
        const { data: refreshData } = await apiClient.post<RefreshResponse | { data: RefreshResponse }>(
          API_ENDPOINTS.AUTH.REFRESH,
          { refresh_token: storedRefreshToken },
        )
        const refreshPayload = unwrapResponse<RefreshResponse>(refreshData)
        const accessToken = refreshPayload?.access_token ?? null
        const newRefreshToken = refreshPayload?.refresh_token ?? null

        if (accessToken) tokenStore.set(accessToken)
        if (newRefreshToken) tokenStore.setRefresh(newRefreshToken)

        const { data: meData } = await apiClient.get<MeResponse | { data: MeResponse }>(
          API_ENDPOINTS.AUTH.ME,
        )
        const mePayload = unwrapResponse<MeResponse>(meData)

        if (!cancelled && mePayload) {
          setState({
            user: mePayload.user,
            organization: mePayload.organization,
            role: mePayload.role,
            isAuthenticated: true,
            isLoading: false,
          })
        }
      } catch {
        if (!cancelled) {
          tokenStore.set(null)
          tokenStore.setRefresh(null)
          setState(UNAUTHENTICATED)
        }
      }
    }

    void restoreSession().finally(() => { clearTimeout(safetyTimer) })
    return () => {
      cancelled = true
      clearTimeout(safetyTimer)
    }
  }, [])

  // ── Login ───────────────────────────────────────────────────────────────
  const login = useCallback(async (email: string, password: string): Promise<void> => {
    const { data } = await apiClient.post<LoginResponse | { data: LoginResponse }>(
      API_ENDPOINTS.AUTH.LOGIN,
      { email, password },
    )

    const payload = unwrapResponse<LoginResponse>(data)
    const accessToken = payload?.access_token ?? null
    const refreshToken = payload?.refresh_token ?? null

    if (!payload || !accessToken) {
      throw new Error('Authentication response was missing an access token.')
    }

    tokenStore.set(accessToken)
    if (refreshToken) tokenStore.setRefresh(refreshToken)

    const { data: meData } = await apiClient.get<MeResponse | { data: MeResponse }>(
      API_ENDPOINTS.AUTH.ME,
    )
    const mePayload = unwrapResponse<MeResponse>(meData)

    if (!mePayload) {
      throw new Error('Authentication response did not include profile data.')
    }

    setState({
      user: mePayload.user,
      organization: mePayload.organization,
      role: mePayload.role,
      isAuthenticated: true,
      isLoading: false,
    })
  }, [])

  // ── Logout ──────────────────────────────────────────────────────────────
  const logout = useCallback(async (): Promise<void> => {
    try {
      const refreshToken = tokenStore.getRefresh()
      await apiClient.post(API_ENDPOINTS.AUTH.LOGOUT, refreshToken ? { refresh_token: refreshToken } : undefined)
    } catch {
      // Best-effort
    } finally {
      tokenStore.set(null)
      tokenStore.setRefresh(null)
      setState(UNAUTHENTICATED)
    }
  }, [])

  // ── Set session (invitation acceptance, OAuth, etc.) ────────────────────
  const setSession = useCallback((payload: SessionPayload): void => {
    tokenStore.set(payload.access)
    setState({
      user: payload.user,
      organization: payload.organization,
      role: payload.role,
      isAuthenticated: true,
      isLoading: false,
    })
  }, [])

  // ── Permission check ────────────────────────────────────────────────────
  const hasPermission = useCallback(
    (requiredRole: UserRole): boolean => {
      if (!state.role) return false
      return ROLE_HIERARCHY[state.role] >= ROLE_HIERARCHY[requiredRole]
    },
    [state.role],
  )

  // ── Refresh org (re-fetch /me and update organization in state) ──────────
  const refreshOrg = useCallback(async (): Promise<void> => {
    try {
      const { data: meData } = await apiClient.get<MeResponse | { data: MeResponse }>(
        API_ENDPOINTS.AUTH.ME,
      )
      const mePayload = unwrapResponse<MeResponse>(meData)
      if (mePayload) {
        setState((prev) => ({
          ...prev,
          organization: mePayload.organization,
          user: mePayload.user,
          role: mePayload.role,
        }))
      }
    } catch {
      // Best-effort -- do not log out on refresh failure
    }
  }, [])

  const value = useMemo<AuthContextValue>(
    () => ({ ...state, login, logout, setSession, hasPermission, refreshOrg }),
    [state, login, logout, setSession, hasPermission, refreshOrg],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

